import { AppUser } from './user.types';

/**
 * Utility functions for checking user roles.
 * Centralized role checking logic to ensure consistency across the application.
 */

/**
 * Checks if a user is a trainer based on various role indicators.
 * Handles multiple role formats for backward compatibility.
 * 
 * @param user - The user object from localStorage or API
 * @returns true if user is a trainer, false otherwise
 */
export function isTrainer(user: AppUser | null | undefined): boolean {
  if (!user || typeof user !== 'object') {
    return false;
  }

  try {
    // Check is_trainer field first (1 = trainer, 0 or null = golfer)
    if (user.is_trainer !== undefined && user.is_trainer !== null) {
      return Number(user.is_trainer) === 1;
    }

    // Check roles array
    if (Array.isArray(user.roles) && user.roles.length > 0) {
      const firstRole = user.roles[0];
      if (firstRole?.name) {
        const roleName = String(firstRole.name).trim().toLowerCase();
        if (roleName === 'trainer') {
          return true;
        }
      }
    }

    // Check direct role field (number: 1=golfer, 2=trainer)
    if (user.role !== undefined && user.role !== null) {
      if (typeof user.role === 'string') {
        return user.role.trim().toLowerCase() === 'trainer';
      }
      return Number(user.role) === 2;
    }

    // Check user_type field (string: 'golfer' or 'trainer')
    if (user.user_type) {
      const userTypeLower = String(user.user_type).trim().toLowerCase();
      return userTypeLower === 'trainer' || userTypeLower === 'instructor';
    }
  } catch (error) {
    // Log error in production but don't throw
    console.error('Error checking trainer role:', error);
    return false;
  }

  return false;
}

/**
 * Checks if a user is a golfer based on various role indicators.
 * Handles multiple role formats for backward compatibility.
 * 
 * @param user - The user object from localStorage or API
 * @returns true if user is a golfer, false otherwise
 */
export function isGolfer(user: AppUser | null | undefined): boolean {
  if (!user || typeof user !== 'object') {
    return false;
  }

  try {
    // Check is_trainer field first (1 = trainer, 0 or null = golfer)
    if (user.is_trainer !== undefined && user.is_trainer !== null) {
      return Number(user.is_trainer) === 0;
    }

    // Check roles array
    if (Array.isArray(user.roles) && user.roles.length > 0) {
      const firstRole = user.roles[0];
      if (firstRole?.name) {
        const roleName = String(firstRole.name).trim().toLowerCase();
        if (roleName === 'golfer') {
          return true;
        }
      }
    }

    // Check direct role field (number: 1=golfer, 2=trainer)
    if (user.role !== undefined && user.role !== null) {
      if (typeof user.role === 'string') {
        return user.role.trim().toLowerCase() === 'golfer';
      }
      return Number(user.role) === 1;
    }

    // Check user_type field (string: 'golfer' or 'trainer')
    if (user.user_type) {
      const userTypeLower = String(user.user_type).trim().toLowerCase();
      return userTypeLower === 'golfer';
    }
  } catch (error) {
    // Log error in production but don't throw
    console.error('Error checking golfer role:', error);
    return false;
  }

  return false;
}

